import { isEmpty } from 'lodash';

import { msg } from 'lib/api';

import SectionType, { FIXED_TITLE_SECTIONS } from 'apps/catalogue/models/SectionType';
import PerUnit from 'apps/catalogue/models/PerUnit';
import containerTypes from 'apps/analytics/utils/containerTypes';

/**
 * @typedef {object} RibbonMetadata
 * @property {string|null} title Title to display on the ribbon
 * @property {string|null} background Background to display on the ribbon
 */

/**
 * @enum {RibbonMetadata}
 */
export const SECTION_TYPE_TO_RIBBON_MAPPING = {
  // standard ribbons
  [SectionType.ADVERT]: {
    title: msg.fop.ribbon.advert,
    background: 'advert',
  },
  [SectionType.ADVERT_ALT]: {
    title: msg.fop.ribbon.featuredAlt,
    background: 'advert',
  },
  [SectionType.CROSS_SELL]: {
    title: null,
    background: 'cross-sell',
  },
  [SectionType.EXTERNAL]: {
    title: msg.fop.ribbon.featured,
    background: 'external',
  },
  [SectionType.EXTERNAL_ALT]: {
    title: msg.fop.ribbon.featuredAlt,
    background: 'external',
  },
  [SectionType.PROMO_BOX]: {
    title: msg.fop.ribbon.featured,
    background: 'promo-box',
  },
  [SectionType.FEATURED]: {
    title: msg.fop.ribbon.featured,
    background: 'featured',
  },
  [SectionType.FAVOURITES]: {
    title: 'In Your Favourites',
    background: 'fav',
  },
  [SectionType.FEATURED_AFTER_FAVS]: {
    title: msg.fop.ribbon.featured,
    background: 'featured-after-fav',
  },
  [SectionType.ON_OFFER]: {
    title: msg.fop.ribbon.on_offer,
    background: 'offer',
  },
  [SectionType.NEW]: {
    title: 'NEW',
    background: 'new',
  },
  [SectionType.RECOMMENDATIONS]: {
    title: 'Recommended to You',
    background: 'recommended',
  },
  [SectionType.SUBSCRIPTIONS]: {
    title: msg.subscriptions.displayName,
    background: 'subs-starters',
  },
  [SectionType.SUGGESTED_ORDER]: {
    title: msg.ordersReserved.suggestedOrder.ribbonDisplayName,
    background: 'other',
  },
  [SectionType.OTHER]: {
    title: 'Other',
    background: 'other',
  },
  // before you go
  [SectionType.HYRO_ITEMS]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.FORGOTTEN_ITEMS]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.INCOMPLETE_OFFERS]: {
    title: null, // TODO add proper name,
    background: null, // TODO add proper class
  },
  [SectionType.OFFERS_ON_FAVS]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.TOP_OFFERS_BESTSELLERS]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.TRY_SOMETHING_NEW]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.FLASH_SALES]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.SPECIAL_OFFERS]: {
    title: null, // TODO add proper name
    background: null, // TODO add proper class
  },
  [SectionType.PERSONALISED_COUPONS]: {
    title: 'Coupons',
    background: 'offer',
  },

  // cluster
  [SectionType.CLUSTER]: {
    title: null, // clusters get name assembled from tags
    background: 'search-no-arrow',
  },
  // temperature
  [SectionType.AMBIENT]: {
    title: 'Ambient',
    background: null, // TODO add proper class
  },
  [SectionType.CHILLED]: {
    title: 'Chilled',
    background: null, // TODO add proper class
  },
  [SectionType.FROZEN]: {
    title: 'Frozen',
    background: null, // TODO add proper class
  },
  DEFAULT: {
    title: null,
    background: null,
  },
};

const PER_UNIT_TO_RIBBON_TITLE_MAPPING = {
  [PerUnit.ONE_HUNDRED_G]: 'Price per 100g',
  [PerUnit.ONE_HUNDRED_ML]: 'Price per 100ml',
  [PerUnit.TEN_G]: 'Price per 10g',
  [PerUnit.TEN_ML]: 'Price per 10ml',
  [PerUnit.ONE_G]: 'Price per 1g',
  [PerUnit.ONE_M]: 'Price per 1m',
  [PerUnit.ONE_ML]: 'Price per 1ml',
  [PerUnit.SEVENFIVE_CL]: 'Price per 75cl',
  [PerUnit.ONE_KG]: 'Price per 1kg',
  [PerUnit.ONE_LITRE]: 'Price per litre',
  [PerUnit.EACH]: 'Price each',
  [PerUnit.OTHER]: 'unclassified',
};

const CONTAINER_TYPE_TITLE_MAPPING = {
  [containerTypes.ALTERNATIVES]: 'Other',
  [containerTypes.SIMILAR_PRODUCTS]: 'Similar Products',
  [containerTypes.YOU_MIGHT_LIKE]: 'You might like',
};

/**
 * Return ribbon metadata for given sectionType
 * @param {SectionType} sectionType SectionType to look for metadata
 * @returns {RibbonMetadata}
 */
export function getRibbonForSectionType(sectionType) {
  return SECTION_TYPE_TO_RIBBON_MAPPING[sectionType] || SECTION_TYPE_TO_RIBBON_MAPPING.DEFAULT;
}

function shouldIgnoreAttributeTitle(sectionType) {
  return sectionType && FIXED_TITLE_SECTIONS.includes(sectionType);
}

function isTypeFromContainerType(sectionType) {
  const containerTypesList = [
    containerTypes.SIMILAR_PRODUCTS,
    containerTypes.ALTERNATIVES,
    containerTypes.YOU_MIGHT_LIKE,
  ];
  return sectionType && containerTypesList.includes(sectionType);
}

export default function getSectionTitle(sectionAttributes) {
  if (isEmpty(sectionAttributes)) {
    return null;
  }
  const { title, /* subtitle, */ type, cluster = {} } = sectionAttributes;
  let ribbonTitle;
  if (title && !shouldIgnoreAttributeTitle(type)) {
    // if title was given, use it
    // exception for translations (Featured)
    ribbonTitle = title;
  } else if (isTypeFromContainerType(type)) {
    ribbonTitle = CONTAINER_TYPE_TITLE_MAPPING[type];
  } else if (type && type !== SectionType.CLUSTER) {
    // if not cluster, take the default value
    ribbonTitle = SECTION_TYPE_TO_RIBBON_MAPPING[type].title;
  } else {
    // otherwise assembly name from cluster data
    const { tags, perUnit } = cluster;
    if (tags) {
      ribbonTitle = tags.map(tag => tag.name).join(' » ');
    }
    if (perUnit) {
      ribbonTitle = ribbonTitle
        ? `${ribbonTitle} » ${PER_UNIT_TO_RIBBON_TITLE_MAPPING[perUnit]}`
        : PER_UNIT_TO_RIBBON_TITLE_MAPPING[perUnit];
    }
  }
  return ribbonTitle;
}
